/**
 * The nav icon set.
 *
 * Drawn here rather than pulled from a library, and drawn rather than picked
 * out of the emoji table: twelve glyphs is not worth a dependency, and an
 * emoji is a different typeface's idea of a picture — it lands at a different
 * weight and colour from everything around it and cannot be tinted by the
 * active-tab colour.
 *
 * One geometry for all of them: a 24×24 box, no fill, `currentColor` stroke at
 * 1.6, round caps and joins. That uniformity is the whole reason a set reads as
 * a set, so a new icon copies these attributes rather than inventing its own —
 * they live on the shared `<Svg>` wrapper precisely so a new one cannot drift.
 *
 * They exist for the phone's bottom bar, where a label alone would either
 * truncate or crowd out its neighbours at five across. The desktop top bar
 * stays text — it has the room, and words are faster to scan than pictures
 * when you are not aiming a thumb at them.
 */

import type { ReactNode } from 'react';

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg
      className="icon"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      // Decorative: every icon in this console sits beside its own text label,
      // so announcing it would read the destination twice.
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Money arriving: into the tray, from above. */
export const DepositIcon = () => (
  <Svg>
    <path d="M12 3v9" />
    <path d="m8.5 8.5 3.5 3.5 3.5-3.5" />
    <path d="M4 14v3.5A2.5 2.5 0 0 0 6.5 20h11a2.5 2.5 0 0 0 2.5-2.5V14" />
  </Svg>
);

/** Money leaving: out of the tray, upward. The deposit glyph, reflected. */
export const WithdrawalIcon = () => (
  <Svg>
    <path d="M12 12V3" />
    <path d="m8.5 6.5 3.5-3.5 3.5 3.5" />
    <path d="M4 14v3.5A2.5 2.5 0 0 0 6.5 20h11a2.5 2.5 0 0 0 2.5-2.5V14" />
  </Svg>
);

export const InboxIcon = () => (
  <Svg>
    <path d="M20 14.5a2.5 2.5 0 0 1-2.5 2.5H9l-4 3v-3H6.5A2.5 2.5 0 0 1 4 14.5v-8A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5Z" />
  </Svg>
);

export const PlayersIcon = () => (
  <Svg>
    <circle cx="9.5" cy="8" r="3" />
    <path d="M3.5 20a6 6 0 0 1 12 0" />
    <path d="M16 5.5a3 3 0 0 1 0 5" />
    <path d="M17.5 14.5a6 6 0 0 1 3 5.5" />
  </Svg>
);

export const DashboardIcon = () => (
  <Svg>
    <path d="M4 20h16" />
    <path d="M7 20v-6" />
    <path d="M12 20V6" />
    <path d="M17 20v-9" />
  </Svg>
);

/** A round: the cage, and the ball that came out of it. */
export const RoundsIcon = () => (
  <Svg>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="2.5" />
  </Svg>
);

/** Game types: the settings that tune them. */
export const GameTypesIcon = () => (
  <Svg>
    <path d="M4 8h10" />
    <path d="M18 8h2" />
    <circle cx="16" cy="8" r="2" />
    <path d="M4 16h4" />
    <path d="M12 16h8" />
    <circle cx="10" cy="16" r="2" />
  </Svg>
);

/** Where the money is sent: a bank. */
export const AgentsIcon = () => (
  <Svg>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
    <path d="M16.5 6.5a3 3 0 0 1 0 5.6" />
    <path d="M18 19.5a5.2 5.2 0 0 0-2.2-4.2" />
  </Svg>
);

export const DestinationsIcon = () => (
  <Svg>
    <path d="M3.5 9.5 12 4.5l8.5 5" />
    <path d="M5.5 9.5v8" />
    <path d="M10 9.5v8" />
    <path d="M14 9.5v8" />
    <path d="M18.5 9.5v8" />
    <path d="M3.5 20h17" />
  </Svg>
);

export const PostersIcon = () => (
  <Svg>
    <rect x="3.5" y="4.5" width="17" height="15" rx="2.5" />
    <circle cx="9" cy="10" r="1.5" />
    <path d="m4.5 17 4.5-4 3.5 3 3-2.5 4 3.5" />
  </Svg>
);

export const AdminsIcon = () => (
  <Svg>
    <path d="M12 3.5 5 6v5.5c0 4 3 7.3 7 9 4-1.7 7-5 7-9V6Z" />
    <path d="m9 12 2.2 2.2L15.5 10" />
  </Svg>
);

export const IpAllowlistIcon = () => (
  <Svg>
    <rect x="4.5" y="10.5" width="15" height="9.5" rx="2" />
    <path d="M8 10.5v-2a4 4 0 0 1 8 0v2" />
  </Svg>
);

/** Reconciliation: the two sides, and whether they balance. */
export const ReconcileIcon = () => (
  <Svg>
    <path d="M12 4v16" />
    <path d="M6 8h12" />
    <path d="M3.5 14 6 8l2.5 6a2.5 2.5 0 0 1-5 0Z" />
    <path d="M15.5 14 18 8l2.5 6a2.5 2.5 0 0 1-5 0Z" />
  </Svg>
);

/** Everything that did not fit in the bar. */
export const MoreIcon = () => (
  <Svg>
    <circle cx="5.5" cy="12" r="1.4" />
    <circle cx="12" cy="12" r="1.4" />
    <circle cx="18.5" cy="12" r="1.4" />
  </Svg>
);

export const CloseIcon = () => (
  <Svg>
    <path d="m6.5 6.5 11 11" />
    <path d="m17.5 6.5-11 11" />
  </Svg>
);

export const BackIcon = () => (
  <Svg>
    <path d="M14.5 5.5 8 12l6.5 6.5" />
  </Svg>
);
